import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors, slugify } from '../../utils/common';

const ORDER_STATUSES = ['pending', 'processing', 'shipped', 'delivered', 'completed', 'cancelled'];

const STATUS_COLORS = {
    completed: 'success',
    delivered: 'success',
    shipped: 'info',
    processing: 'warning',
    pending: 'warning',
    draft: 'secondary',
    cancelled: 'danger',
};

const PAYMENT_STATUS_COLORS = {
    paid: 'success',
    failed: 'danger',
};

const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const OrderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/order/${id}`);
                if (res.status === 200) {
                    setOrder(res.data);
                    setSelectedStatus(res.data.status || '');
                }
            } catch (err) {
                handleErrors(err);
                navigate(ROUTES.ORDERS);
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [id, navigate]);

    const handleUpdateStatus = async () => {
        if (!selectedStatus || selectedStatus === order?.status) return;
        try {
            setUpdating(true);
            const res = await api.patch(`/order/${id}/status`, { status: selectedStatus });
            if (res.status === 200) {
                showToast('success', res.data.message || 'Order status updated', 'Updated');
                setOrder(prev => ({ ...prev, status: selectedStatus }));
            }
        } catch (err) {
            handleErrors(err);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!order) return null;

    const items = order.items || [];
    const subTotal = order.sub_total || 0;
    const shipping = order.shipping_charges || 0;
    const tax = order.tax || 0;
    const grandTotal = order.total_amount || 0;
    const payment = order.payment;
    const customerName = `${order.first_name || ''} ${order.last_name || ''}`.trim();
    // Drafts are checkouts that haven't been paid yet, so they can't move through fulfilment
    const isDraft = order.status === 'draft';

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ORDERS}>Orders</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        {order.order_number || 'Order Detail'}
                    </li>
                </ol>
            </div>

            <div className="row">
                {/* Order Info Card */}
                <div className="col-lg-8">
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h4 className="card-title mb-0">
                                Order <strong>{order.order_number}</strong>
                            </h4>
                            <span className={`badge badge-${STATUS_COLORS[order.status] || 'secondary'} fs-6`}>
                                {order.status}
                            </span>
                        </div>
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-sm-6">
                                    <p className="mb-1"><strong>Order Date:</strong> {formatDate(order.order_date)}</p>
                                    <p className="mb-1"><strong>Payment Method:</strong> {order.payment_method || 'N/A'}</p>
                                    <p className="mb-1">
                                        <strong>Payment Status:</strong>{' '}
                                        <span className={`badge badge-${PAYMENT_STATUS_COLORS[order.payment_status] || 'warning'}`}>
                                            {order.payment_status || 'N/A'}
                                        </span>
                                    </p>
                                    <p className="mb-1"><strong>Shipping Method:</strong> {order.shipping_method || 'N/A'}</p>
                                    <p className="mb-1"><strong>Tracking No.:</strong> {order.order_number || 'N/A'}</p>
                                    <p className="mb-1"><strong>Est. Dispatch:</strong> {order.estimated_dispatch || 'N/A'}</p>
                                </div>
                                <div className="col-sm-6">
                                    <p className="mb-1"><strong>Shipping Address:</strong></p>
                                    <p className="text-muted mb-2" style={{ whiteSpace: 'pre-line' }}>{order.shipping_address || 'N/A'}</p>
                                    <p className="mb-1"><strong>Billing Address:</strong></p>
                                    <p className="text-muted" style={{ whiteSpace: 'pre-line' }}>{order.billing_address || 'N/A'}</p>
                                </div>
                            </div>

                            {order.vin && (
                                <p className="mb-1"><strong>VIN:</strong> {order.vin}</p>
                            )}
                            {order.order_notes && (
                                <p className="mb-1"><strong>Order Notes:</strong> {order.order_notes}</p>
                            )}

                            {/* Items Table */}
                            <h5 className="mt-4 mb-3">Order Items</h5>
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Product</th>
                                            <th className="text-end">Unit Price</th>
                                            <th className="text-center">Qty</th>
                                            <th className="text-end">Line Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length > 0 ? items.map((item, i) => (
                                            <tr key={item.product_id || i}>
                                                <td>{i + 1}</td>
                                                <td>
                                                    <Link to={`/products/${slugify(item.title)}/edit`} className="text-primary">
                                                        {item.title || 'N/A'}
                                                    </Link>
                                                </td>
                                                <td className="text-end">AED {(item.price || 0).toFixed(2)}</td>
                                                <td className="text-center">{item.qty || 0}</td>
                                                <td className="text-end">AED {(item.total || 0).toFixed(2)}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="text-center">No items found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="col-lg-4">
                    {/* Customer */}
                    <div className="card h-auto">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="card-title mb-0">Customer</h5>
                            <span>
                                {order.payment_link_id && (
                                    <span className="badge badge-warning me-1">Payment link</span>
                                )}
                                <span className={`badge badge-${order.is_guest ? 'info' : 'primary'}`}>
                                    {order.is_guest ? 'Guest' : 'Registered'}
                                </span>
                            </span>
                        </div>
                        <div className="card-body">
                            <p className="mb-1"><strong>Name:</strong> {customerName || 'N/A'}</p>
                            <p className="mb-1 text-break"><strong>Email:</strong> {order.email ? <a href={`mailto:${order.email}`}>{order.email}</a> : 'N/A'}</p>
                            <p className="mb-1"><strong>Phone:</strong> {order.phone || 'N/A'}</p>
                            {order.company && (
                                <p className="mb-1"><strong>Company:</strong> {order.company}</p>
                            )}
                        </div>
                    </div>

                    {/* Payment */}
                    <div className="card h-auto">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Payment</h5>
                        </div>
                        <div className="card-body">
                            {payment ? (
                                <>
                                    <p className="mb-1"><strong>Gateway:</strong> Telr</p>
                                    <p className="mb-1 text-break"><strong>Transaction Ref:</strong> {payment.transaction_ref || 'N/A'}</p>
                                    <p className="mb-1 text-break"><strong>Telr Order Ref:</strong> {payment.order_ref}</p>
                                    <p className="mb-1 text-break"><strong>Cart ID:</strong> {payment.cart_id || 'N/A'}</p>
                                    <p className="mb-1"><strong>Paid At:</strong> {formatDate(payment.paid_at)}</p>
                                    {order.payment_status !== 'paid' && (
                                        <p className="text-warning mt-2 mb-0">
                                            Payment was started on Telr but hasn't been confirmed. Check the transaction in the Telr merchant admin.
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="text-muted mb-0">No online payment was made for this order.</p>
                            )}
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div className="card h-auto">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Order Summary</h5>
                        </div>
                        <div className="card-body">
                            <table className="table table-borderless mb-0">
                                <tbody>
                                    <tr>
                                        <td>Subtotal</td>
                                        <td className="text-end">AED {subTotal.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Shipping</td>
                                        <td className="text-end">AED {shipping.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Tax</td>
                                        <td className="text-end">AED {tax.toFixed(2)}</td>
                                    </tr>
                                    <tr className="fw-bold border-top">
                                        <td>Grand Total</td>
                                        <td className="text-end">AED {grandTotal.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="card-header">
                            <h5 className="card-title mb-0">Update Status</h5>
                        </div>
                        <div className="card-body">
                            {isDraft && (
                                <p className="text-muted">This order is awaiting payment, so its status can't be changed.</p>
                            )}
                            <select
                                className="form-select mb-3"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                disabled={isDraft}
                            >
                                {isDraft && <option value="draft">Draft</option>}
                                {ORDER_STATUSES.map(s => (
                                    <option key={s} value={s}>
                                        {s.charAt(0).toUpperCase() + s.slice(1)}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleUpdateStatus}
                                disabled={isDraft || updating || selectedStatus === order.status}
                            >
                                {updating ? (
                                    <><span className="spinner-border spinner-border-sm me-1"></span> Updating...</>
                                ) : 'Update Status'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-footer">
                            <Link to={ROUTES.ORDERS} className="btn btn-outline-secondary">
                                <i className="fas fa-arrow-left me-1"></i> Back to Orders
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
};

export default OrderDetail;
